import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tooltip } from 'primereact/tooltip';
import NewButton from '../../../../components/NewButton/NewButton';
import { useLanguage } from '../../../../contexts/LanguageContext';

const CustomerDefinitionTable = ({ customers, totalRecords, loading, handleNew, handleEdit, pagination, onPageChange }) => {
    const { th } = useLanguage();
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);
    const [sortedCustomers, setSortedCustomers] = useState([]);

    useEffect(() => {
        if (!customers || customers.length === 0) {
            setSortedCustomers([]);
            return;
        }

        let sorted = [...customers];

        if (sortField) {
            sorted.sort((a, b) => {
                let aValue = a[sortField];
                let bValue = b[sortField];

                if (aValue == null) return sortOrder === 1 ? 1 : -1;
                if (bValue == null) return sortOrder === 1 ? -1 : 1;

                if (typeof aValue === 'string') {
                    aValue = aValue.toLowerCase();
                    bValue = bValue.toLowerCase();
                }

                if (aValue < bValue) return sortOrder === 1 ? -1 : 1;
                if (aValue > bValue) return sortOrder === 1 ? 1 : -1;
                return 0;
            });
        }

        setSortedCustomers(sorted);
    }, [customers, sortField, sortOrder]);

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2">
                <i onClick={() => handleEdit(rowData)} className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700" style={{ left: '1.5rem', color: '#0B3187' }}></i>
                <i className="pi pi-history cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('warrantyClaims.tooltips.history')}></i>
            </div>
        );
    };

    // Çoklu şasi gösterimi için body template - virgülle ayrılmış ve tooltip ile tam liste
    const chassisBodyTemplate = (rowData) => {
        // Önce chassis array'ini kontrol et, yoksa eski chassisNo alanını kullan
        const chassisList = rowData.chassis || [];
        
        if (chassisList.length === 0) {
            // Geriye dönük uyumluluk: eski tek şasi alanı
            return rowData.chassisNo || '-';
        }

        const chassisNumbers = chassisList.map(c => c.chassisNo);
        const displayText = chassisNumbers.join(', ');
        const uniqueId = `chassis-tooltip-${rowData.id}`;

        // Uzun ise kısalt ve tooltip göster
        if (displayText.length > 30) {
            return (
                <>
                    <Tooltip target={`.${uniqueId}`} content={displayText} position="top" />
                    <span className={uniqueId} style={{ cursor: 'help' }}>
                        {displayText.substring(0, 27)}...
                    </span>
                </>
            );
        }

        return displayText;
    };

    // Çoklu plaka gösterimi için body template
    const licensePlateBodyTemplate = (rowData) => {
        const chassisList = rowData.chassis || [];
        
        if (chassisList.length === 0) {
            // Geriye dönük uyumluluk: eski tek plaka alanı
            return rowData.licensePlate || '-';
        }

        const plates = chassisList.map(c => c.licensePlate).filter(p => p);
        
        if (plates.length === 0) return '-';

        const displayText = plates.join(', ');
        const uniqueId = `plate-tooltip-${rowData.id}`;

        // Uzun ise kısalt ve tooltip göster
        if (displayText.length > 20) {
            return (
                <>
                    <Tooltip target={`.${uniqueId}`} content={displayText} position="top" />
                    <span className={uniqueId} style={{ cursor: 'help' }}>
                        {displayText.substring(0, 17)}...
                    </span>
                </>
            );
        }

        return displayText;
    };

    const header = (
        <div className="flex align-items-center justify-content-end">
            <div className="flex justify-content-end">
                <NewButton label={th('customerDefinition.newTitle')} onClick={handleNew} />
            </div>
        </div>
    );

    const handlePage = (event) => {
        const newPagination = {
            PageNumber: Math.floor(event.first / event.rows) + 1,
            PageSize: event.rows
        };
        onPageChange(newPagination);
    };

    const handleSort = (event) => {
        setSortField(event.sortField);
        setSortOrder(event.sortOrder);
    };

    return (
        <DataTable
            value={sortedCustomers}
            lazy
            paginator
            first={(pagination.PageNumber - 1) * pagination.PageSize}
            rows={pagination.PageSize}
            totalRecords={totalRecords}
            onPage={handlePage}
            onSort={handleSort}
            sortField={sortField}
            sortOrder={sortOrder}
            className="p-datatable-gridlines"
            showGridlines
            dataKey="id"
            loading={loading}
            header={header}
            emptyMessage={th('customerDefinition.emptyMessage')}
            rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000]}
            tableStyle={{ minWidth: '50rem' }}
            rowKey="id"
        >
            <Column key="actions" bodyClassName="text-center" headerClassName="text-center" body={actionBodyTemplate} exportable={false} style={{ minWidth: '33px' }}></Column>
            <Column key="name" field="name" header={th('customerDefinition.columnHeaders.firstName')} sortable style={{ width: '15%' }} />
            <Column key="surname" field="surname" header={th('customerDefinition.columnHeaders.lastName')} sortable style={{ width: '15%' }} />
            <Column key="identityNo" field="identityNo" header={th('customerDefinition.columnHeaders.tcNumber')} sortable style={{ width: '15%' }} />
            <Column key="phoneNumber" field="phoneNumber" header={th('customerDefinition.columnHeaders.phone')} sortable style={{ width: '15%' }} />
            <Column key="address" field="address" header={th('customerDefinition.columnHeaders.address')} sortable style={{ width: '20%' }} />
            <Column key="country" field="country" header={th('customerDefinition.columnHeaders.country')} sortable style={{ width: '10%' }} />
            <Column key="city" field="city" header={th('customerDefinition.columnHeaders.city')} sortable style={{ width: '10%' }} />
            <Column key="chassisNo" header={th('customerDefinition.columnHeaders.chassisNo')} body={chassisBodyTemplate} style={{ width: '15%' }} />
            <Column key="licensePlate" header={th('customerDefinition.columnHeaders.licensePlate')} body={licensePlateBodyTemplate} style={{ width: '10%' }} />
        </DataTable>
    );
};

export default CustomerDefinitionTable;
